import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Menu, X } from 'lucide-react';

const Instagram = (props: any) => (
  <svg 
    {...props} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';

const navItems = [
  { label: 'Home', href: '/#home' },
  { label: 'Rooms', href: '/#rooms' },
  { label: 'Amenities', href: '/#amenities' },
  { label: 'Experiences', href: '/#experiences' },
  { label: 'Gallery', href: '/#gallery' },
  { label: 'Location', href: '/#location' },
  { label: 'Store', href: '/store' },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [settings, setSettings] = useState<any>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from('site_settings').select('*').single();
      if (data) setSettings(data);
    };
    fetchSettings();

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = ['home', 'rooms', 'amenities', 'experiences', 'gallery', 'location'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatsappLink = `https://wa.me/${settings?.whatsapp_number || '918900992020'}?text=${encodeURIComponent(
    "Hello Walnut Farm Estate, I would like to enquire about availability and booking. Please share the available dates and stay details."
  )}`;

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-700 px-4 md:px-6 lg:px-12 w-full box-border",
        isScrolled 
          ? "bg-background/95 backdrop-blur-md shadow-sm py-3 md:py-4 border-b border-primary/5" 
          : "bg-transparent py-4 md:py-8"
      )}
    >
      <div className="max-w-screen-2xl mx-auto flex items-center justify-between w-full">
        <a href="/" className="relative z-[60] flex items-center group gap-2 md:gap-3 flex-shrink-0 max-w-[80%]">
          <img 
            src={settings?.logo_url || "/assets/logo.png"} 
            alt="Walnut Farm" 
            className={cn(
              "transition-all duration-700 object-contain",
              isScrolled ? "h-10 md:h-12 w-auto" : "h-14 md:h-20 w-auto"
            )}
            style={{ width: isScrolled ? 'clamp(48px, 15vw, 64px)' : 'clamp(58px, 18vw, 80px)' }}
          />
          <div className="flex flex-col items-start justify-center overflow-hidden">
            <span className={cn(
              "transition-all duration-700 leading-[1.1] font-clean font-bold",
              "text-[#176B5B]",
              isScrolled 
                ? "text-[20px] md:text-[28px] tracking-[0.02em]" 
                : "text-[28px] md:text-[42px] tracking-[0.02em]"
            )}>
              {settings?.site_name || 'Walnut Farm'}
            </span>
            <span className={cn(
              "transition-all duration-700 leading-[1] mt-0.5 font-clean font-medium uppercase",
              isScrolled 
                ? "text-[10px] md:text-[12px] tracking-[0.1em] opacity-70" 
                : "text-[12px] md:text-[14px] tracking-[0.15em] opacity-80",
              isScrolled ? "text-primary" : "text-white"
            )}>
              Private Luxury Farmhouse
            </span>
          </div>
        </a>

        <div className="hidden lg:flex items-center space-x-8">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={cn(
                "relative text-[16px] md:text-[20px] font-medium transition-all duration-300 opacity-90 hover:opacity-100 font-clean font-bold uppercase tracking-widest text-xs",
                isScrolled ? "text-primary" : "text-white",
                (activeSection === item.href.split('#')[1] || (item.href === '/#home' && activeSection === 'home')) && "opacity-100"
              )}
            >
              {item.label}
              {(activeSection === item.href.split('#')[1] || (item.href === '/#home' && activeSection === 'home')) && (
                <motion.div
                  layoutId="navUnderline"
                  className={cn(
                    "absolute -bottom-1 left-0 right-0 h-[1px]",
                    isScrolled ? "bg-accent" : "bg-white"
                  )}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center space-x-6">
          <div className="flex items-center space-x-4">
            {settings?.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "transition-all duration-300 hover:scale-110",
                  isScrolled ? "text-primary/60 hover:text-primary" : "text-white/60 hover:text-white"
                )}
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}
          </div>

          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "px-6 py-2.5 rounded-full text-[13px] font-bold transition-all duration-500 whitespace-nowrap font-clean uppercase tracking-wider",
              isScrolled
                ? "bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/10"
                : "bg-white/10 backdrop-blur-md text-white border border-white/20 hover:bg-white/20"
            )}
          >
            Enquire on WhatsApp
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={cn(
            "lg:hidden relative z-[60] p-2 transition-colors",
            mobileMenuOpen || isScrolled ? "text-primary" : "text-white"
          )}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[100] bg-[#F3EFE6] flex flex-col p-8 lg:hidden shadow-2xl"
          >
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-6 right-6 p-2 text-primary"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="mt-24 space-y-6">
              {navItems.map((item, idx) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-2xl font-clean font-bold text-primary hover:text-accent transition-colors py-2 uppercase tracking-tighter"
                >
                  {item.label}
                </motion.a>
              ))}
            </div>

            <div className="mt-auto pb-12">
              <div className="flex justify-center space-x-8 pb-8 border-t border-primary/5 pt-8">
                {settings?.instagram_url && (
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="text-primary/60 hover:text-primary">
                    <Instagram className="w-6 h-6" />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[9999] bg-[#25D366] text-white p-3 md:p-4 rounded-full shadow-2xl flex items-center justify-center border border-white/20"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </motion.a>
    </nav>
  );
}
